/**
 * @file RgsClientFactory.ts
 * @role Builds the RGS client at the composition boundary. There is only one IRgsClient
 *       implementation now — the mode decides who ANSWERS the HTTP call (the MSW fake server
 *       or the deployed RGS), never which client speaks. Fakes are allowed only in dev/test
 *       wiring; production fails loud.
 * @owns Backend-client construction and the base-URL requirement.
 * @reads The resolved RgsMode and the validated game config.
 * @neverDoes Decide outcomes. Validate backend payloads. Touch Pixi.
 * @invariants 1, 6, 13, 16.
 * @see docs/BACKEND_CONTRACT.md, src/core/backend/RgsMode.ts
 */
import type { GameConfig } from '@core/config/ConfigSchema';
import { GameError } from '@core/errors/GameError';
import { HttpRgsClient } from './HttpRgsClient';
import type { IRgsClient } from './IRgsClient';
import { resolveRgsMode } from './RgsMode';
import type { RgsModeEnvironment } from './RgsMode';

export interface RgsClientEnvironment extends RgsModeEnvironment {
  /** Build-time override for the RGS base URL; empty means "use the config value". */
  readonly rgsBaseUrl?: string;
}

export interface RgsClientFactoryParams {
  environment: RgsClientEnvironment;
  gameConfig: GameConfig;
}

export function createRgsClient(params: RgsClientFactoryParams): IRgsClient {
  const { environment, gameConfig } = params;

  // Resolving is what enforces the production refusal (Invariant 6). Past this point every mode
  // builds the same client against the same base URL; in the fake modes the MSW service worker
  // answers it instead of the network.
  resolveRgsMode(environment, gameConfig);

  const baseUrl = environment.rgsBaseUrl?.trim() || gameConfig.rgs.baseUrl;
  if (!baseUrl) {
    throw new GameError(
      'CONFIG_LOAD_FAILED',
      'No RGS base URL configured; set VITE_RGS_BASE_URL or game.config.json rgs.baseUrl.',
    );
  }

  return new HttpRgsClient({
    baseUrl,
    gameId: gameConfig.rgs.gameId,
    brand: gameConfig.rgs.brand,
    userId: gameConfig.rgs.userId,
    scatterSymbolId: gameConfig.rgs.scatterSymbolId,
  });
}
